import FusionTheme from "@directv-digital/fusion.themes.fusion";
import { Mode, Size, TextFieldStyles } from "./text-field.types";

export const textFieldStyles = (
  mode: Mode,
  size: Size,
  disabled: boolean,
  error: boolean,
  shrinkLabelActive: boolean
): TextFieldStyles => ({
  InputLabel: {
    ...FusionTheme.typography["Body 1"],
    top: {
      sm: "-2px",
      md: "-2px",
      lg: "-2px"
    },
    "&.MuiInputLabel-shrink": {
      top: "2px",
      left: "2px",
      ...FusionTheme.typography["Body 3"],
      ...error && {
        color: mode === "dark" ?
            `${FusionTheme.fusion.palette["field-inverse-label-error"]} !important` :
            `${FusionTheme.fusion.palette["field-label-error"]} !important`
      }
    },
    "& legend": {
      marginLeft: "200px"
    }
  },
  InputLabelStyle: {
    color:
      disabled ? FusionTheme.fusion.palette["field-inactive"] :
        mode === "dark" ? FusionTheme.fusion.palette["field-inverse-label"] :
          FusionTheme.fusion.palette["field-label"]
  },
  InputContainer: {
    "input::-ms-reveal, input::-ms-clear": {
      display: "none"
    },
    // ///////////////////////////////////////////////////////////////
    // used to remove up and down arrows when text field is type number
    "& input[type=number]": {
      MozAppearance: "textfield"
    },
    "& input[type=number]::-webkit-outer-spin-button": {
      WebkitAppearance: "none",
      margin: 0
    },
    "& input[type=number]::-webkit-inner-spin-button": {
      WebkitAppearance: "none",
      margin: 0
    },
    // //////////////////////////////////////////////////////////////
    cursor: "pointer",
    position: "relative",
    zIndex: 900,
    margin: "0px !important",
    "& .MuiInputBase-root": {
      height: {
        xs: "48px !important",
        md: size === "small" ? "48px !important" : "56px !important"
      },
      paddingLeft: FusionTheme.fusion.space.spacing05,
      paddingRight: "0px"
    },
    "& .MuiInputLabel-root": {
      lineHeight: "20.8px !important",
      color: mode === "dark" ?
        FusionTheme.fusion.palette["field-inverse-label"] :
        FusionTheme.fusion.palette["field-label"],
      transform: shrinkLabelActive ?
          `translate(14px, ${size === 'small' ? '-10px' : '-9px'}) !important` :
        {
          xs: `translate(14px, ${size === 'small' ? '16px' : '16px'}) !important`,
          lg: `translate(14px, ${size === 'small' ? '16px' : '18px'}) !important`
        },
      fontSize: {
        xs: `${shrinkLabelActive ? "14" : "16"}px !important`,
        md: `${shrinkLabelActive ? "14" : "20"}px !important`
      }
    },
    "& .MuiInputBase-input": {
      ...FusionTheme.typography["Body 1"],
      fontFamily: "roboto-regular!important",
      paddingLeft: 0,
      color: mode === "dark" ?
        FusionTheme.fusion.palette["field-inverse-input"] :
        FusionTheme.fusion.palette["field-input"],
      // height + padding should equal field height
      height: size === "small" ? "24px" : "32px",
      padding: "14px 10px inherit",
      "&::placeholder": {
        color: mode === "dark" ?
          FusionTheme.fusion.palette["field-inverse-placeholder"] :
          FusionTheme.fusion.palette["field-placeholder"],
        opacity: 1
      },
      "&.Mui-disabled": {
        WebkitTextFillColor: FusionTheme.fusion.palette["field-inactive"]
      }
    },

    "& svg": {
      height: "24px",
      width: "24px"
    },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-active"] :
            FusionTheme.fusion.palette["field-border-active"],
        "& legend": {
          fontSize: "17px !important"
        },
        "& span": { paddingRight: 0 }
      },
      "&.Mui-disabled fieldset": {
        borderColor: mode === "dark" ?
          FusionTheme.fusion.palette["field-inverse-border-inactive"] :
          FusionTheme.fusion.palette["field-border-inactive"]
      },
      "&:hover fieldset": {
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-hover"] :
            FusionTheme.fusion.palette["field-border-hover"]
      },
      "&.Mui-focused fieldset": {
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-engaged"] :
            FusionTheme.fusion.palette["field-border-engaged"]
      }
    },

    "& .MuiOutlinedInput-input": {
      "&:-webkit-autofill": {
        WebkitBoxShadow:
            mode === "dark" ?
              `0 0 0 100px ${FusionTheme.fusion.palette.blue900} inset` :
              `0 0 0 100px ${FusionTheme.fusion.palette.neutral_white} inset`,
        WebkitTextFillColor:
            mode === "dark" ?
              FusionTheme.fusion.palette["field-inverse-input"] :
              FusionTheme.fusion.palette["field-input"]
      }
    },

    "& .Mui-error": {
      "& fieldset": {
        borderWidth: "1px !important",
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
            `${FusionTheme.fusion.palette["field-border-error"]}!important`
      },
      "&:hover fieldset": {
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-hover-error"] :
            FusionTheme.fusion.palette["field-border-hover-error"]
      },
      "&.Mui-focused fieldset": {
        borderColor: disabled ?
          mode === "dark" ?
            FusionTheme.fusion.palette["field-inverse-border-inactive"] :
            FusionTheme.fusion.palette["field-border-inactive"] :
          mode === "dark" ?
            FusionTheme.fusion.palette[
              "field-inverse-border-engaged-error"
            ] :
            FusionTheme.fusion.palette["field-border-engaged-error"]
      }
    },
    "& .MuiFormHelperText-root": {
      padding: 0,
      margin: 0
    }
    // ...FusionTheme.typography["Body 1"],
  },
  AdornmentIcon: {
    padding: FusionTheme.fusion.space.spacing04,
    marginRight: FusionTheme.fusion.space.spacing02,
    "&.Mui-focusVisible": {
      outline: `4px solid ${FusionTheme.fusion.palette["accessibility-focusRing"]}`,
      outlineOffset: "2px"
    }
  },
  ClearIconFill: FusionTheme.fusion.palette["field-icon"],
  SearchIconFill:
    disabled ? FusionTheme.fusion.palette["field-icon-inactive"] :
      mode === "dark" ? FusionTheme.fusion.palette["field-inverse-icon-control"] :
        FusionTheme.fusion.palette["field-icon-control"]
})
